import axios from 'axios';
import { TokenService } from '@utriper/core';
import { getSession, setSession, removeSession } from 'core/api/session';
import { toast } from 'react-toastify';

// This build IS the host client. Sourced from @utriper/core so the authserver's client_id contract
// (a TokenService label) stays the single source of truth -- never a hardcoded 'host' string.
export const CLIENT_ID = TokenService.getLabel(TokenService.IDS.host);

export const Method = {
    GET: 'GET',
    PUT: 'PUT',
    PATCH: 'PATCH',
    POST: 'POST',
    DELETE: 'DELETE',
};

export const Server = {
    API: 'api',
    AUTH: 'auth',
};

const createClient = (baseURL, withCredentials = false) => {
    const client = axios.create({
        baseURL,
        timeout: 60000,
        withCredentials,
    });

    client.interceptors.request.use((config) => {
        const token = getSession();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    });

    return client;
};

const apiClient = createClient(process.env.REACT_APP_API_URL);
const authClient = createClient(process.env.REACT_APP_AUTH_SERVER_URL, true);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

// Called by logout. Without this, a refresh already in flight can resolve AFTER the session has
// been cleared and write a fresh accessToken straight back into localStorage -- which looks exactly
// like "logout works, but only sometimes". The stale Authorization default has to go too: the
// request interceptor only ever ADDS the header, so once set it is never removed, and requests
// after logout would keep carrying the old bearer.
export const abortRefresh = () => {
    processQueue(new Error('Logged out'), null);
    isRefreshing = false;
    delete apiClient.defaults.headers['Authorization'];
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // 401 means "who are you?" -- the token is missing, invalid or expired, so a refresh is
        // worth trying. 403 means "you may not do this", and refreshing cannot help.
        //
        // The apiserver used to answer 403 for BOTH, so this branch caught authorization failures
        // too: a user who merely lacked a role triggered a refresh and, if their refresh cookie had
        // expired, got their session cleared and was bounced to the login screen. A permission error
        // logged people out.
        if (error?.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return apiClient(originalRequest);
                    })
                    .catch((err) => {
                        throw err;
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const { data } = await axios.post(
                    '/api/auth/refresh',
                    { client_id: CLIENT_ID },
                    {
                        baseURL: process.env.REACT_APP_AUTH_SERVER_URL,
                        withCredentials: true,
                    }
                );

                if (!data?.data?.accessToken) {
                    throw error;
                }

                const newToken = data?.data?.accessToken;
                setSession(newToken);
                apiClient.defaults.headers['Authorization'] = `Bearer ${newToken}`;
                processQueue(null, newToken);

                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                removeSession();
                window.location.href = `${process.env.REACT_APP_AUTH_CLIENT_URL}/?next=${process.env.REACT_APP_CLIENT ?? 'host'}`;
                throw refreshError;
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export const fetch = async (url, config) => {
    const { authenticated = false, server = Server.API, ...options } = config;

    const client = server === Server.AUTH ? authClient : apiClient;

    const request = {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
        url,
    };

    if (authenticated) {
        const token = getSession();
        if (token) {
            request.headers['Authorization'] = `Bearer ${token}`;
        }
    }

    try {
        const response = await client.request(request);

        if (response?.data?.errors?.length) {
            response.data.errors.forEach(({ msg, code }) => {
                toast.error(`${msg} (Error code ${code})`, {
                    position: 'top-right',
                    theme: 'colored',
                });
            });
        }

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getPaging = (options = {}) => {
    let order = '';
    if (options.pager.column) {
        order += `&column=${options.pager.column}`;
        if (options.pager.direction) {
            order += `&direction=${options.pager.direction}`;
        }
    }
    return `page=${parseInt(options.pager.page) || 1}&pageSize=${parseInt(options.pager.pageSize) || 30}${order}`;
};
